import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Popup } from "semantic-ui-react";
import styles from "./ContextualHelp.module.scss";
// icons
import QuestionIcon from "app/_assets/icon/question-icon/QuestionIcon";
import AboutIcon from "app/_assets/icon/about-icon/AboutIcon";

const fontSizesByType = {
  mini: { header: "1.14285714em", content: ".78571429rem" },
  tiny: { header: "1.14285714em", content: ".85714286rem" },
  small: { header: "1.14285714em", content: ".92857143rem" },
  large: { header: "1.14285714em", content: "1.14285714rem" },
  huge: { header: "1.14285714em", content: "1.42857143rem" },
};

const positions = {
  bottom: "bottom left",
  top: "top left",
  right: "right center",
};

const icons = {
  info: <AboutIcon />,
  help: <QuestionIcon />,
};

export default function ContextualHelp({
  on,
  type,
  content,
  contentSize,
  header,
  position,
  link: { to, label, external },
  size,
  positionFixed,
  pinned,
  offsetTop,
  offsetLeft,
}) {
  const [isOverThePopup, setIsOverThePopup] = useState(false);

  const sizesToStyle = useMemo(
    () => ({
      "--font-size-content": fontSizesByType[size].content,
      "--font-size-header": fontSizesByType[size].header,
      "--content-size": `${contentSize}px`,
      "--offset-top": `${offsetTop}px`,
      "--offset-left": `${offsetLeft}px`,
    }),
    [size, offsetTop, offsetLeft, contentSize]
  );

  const triggerRef = useCallback((node) => {
    if (node !== null) {
    }
  }, []);

  const linkElement = useMemo(() => {
    if (!to || !label) return null;
    return external ? (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
      >
        {label}
      </a>
    ) : (
      <Link to={to} className={styles.link}>
        {label}
      </Link>
    );
  }, [to, label, external]);

  return (
    <Popup
      trigger={
        <div
          className={`${styles.wrapperIcon} ${
            styles[isOverThePopup ? "isOver" : "isNotOver"]
          }`}
          ref={triggerRef}
        >
          {icons[type]}
        </div>
      }
      basic
      on={on}
      className={`${styles[type]} ${styles[position]} ${styles[size]}`}
      style={sizesToStyle}
      position={positions[position]}
      size={size}
      positionFixed={positionFixed}
      hoverable
      pinned={pinned}
    >
      <div
        onMouseEnter={() => setIsOverThePopup(true)}
        onMouseLeave={() => setIsOverThePopup(false)}
      >
        {header && <Popup.Header>{header}</Popup.Header>}
        <Popup.Content>
          {content}
          {linkElement}
        </Popup.Content>
      </div>
    </Popup>
  );
}

ContextualHelp.defaultProps = {
  type: "info",
  position: "bottom",
  link: { to: undefined, label: undefined, external: false },
  size: "tiny",
  positionFixed: true,
  on: ["focus", "hover"],
  pinned: false,
  offsetTop: 0,
  offsetLeft: 0,
};

ContextualHelp.propTypes = {
  type: PropTypes.oneOf(["info", "help"]),
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  header: PropTypes.string,
  position: PropTypes.oneOf(["top", "bottom", "right"]),
  link: PropTypes.shape({
    label: PropTypes.string,
    to: PropTypes.string,
    external: PropTypes.bool,
  }),
  size: PropTypes.oneOf(["mini", "tiny", "small", "large", "huge"]),
  positionFixed: PropTypes.bool,
  on: PropTypes.oneOfType([
    PropTypes.oneOf(["hover", "click", "focus"]),
    PropTypes.arrayOf(PropTypes.oneOf(["hover", "click", "focus"])),
  ]),
  pinned: PropTypes.bool,
  offsetTop: PropTypes.number,
  offsetLeft: PropTypes.number,
};
